import { showError } from '@redux/actions';
import { store } from '@redux/store';
import AxiosDefault, { AxiosRequestConfig, HttpStatusCode } from 'axios';
import { Configs, Enums, Paths } from '../constants';
import { forceLogout } from '@redux/actions/authAction';
import { updateTokens } from '@redux/reducers/authSlice';

class Axios {
  private axiosInstance = AxiosDefault.create({
    baseURL: Configs.BASE_URL,
    timeout: Configs.REQUEST_TIMEOUT,
  });

  constructor() {
    this.axiosInstance.interceptors.request.use(
      config => {
        const accessToken = store.getState().auth.jwtAccessToken;
        const tenantId = store.getState().facility.tenant?.id;
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        if (tenantId) {
          config.headers['X-Tenant-Id'] = tenantId;
        }
        return config;
      },
      error => {
        // handle request error
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;

        if (
          error.response?.status === HttpStatusCode.Unauthorized &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          try {
            const refreshToken = store.getState().auth.jwtAccessToken;
            const { data } = await AxiosDefault.post(
              `${Configs.BASE_URL}${Paths.REFRESH_TOKEN}`,
              null,
              {
                headers: {
                  Authorization: `Bearer ${refreshToken}`,
                },
              }
            );

            store.dispatch(
              updateTokens({
                accessToken: data.token.accessToken,
              })
            );

            originalRequest.headers.Authorization = `Bearer ${data.token.accessToken}`;
            return this.axiosInstance(originalRequest);
          } catch (refreshError) {
            store.dispatch(forceLogout());
            return Promise.reject(refreshError);
          }
        }

        const status = error.response?.status;

        if (
          status === HttpStatusCode.NotFound ||
          status === HttpStatusCode.Gone
        ) {
          return Promise.reject(error);
        }

        switch (error.response?.status) {
          case HttpStatusCode.BadRequest: {
            const message = error.response.data?.message || 'Bad Request';
            store.dispatch(showError({ message }));
            break;
          }
          case HttpStatusCode.Forbidden:
            store.dispatch(forceLogout());
            break;

          case HttpStatusCode.Conflict:
            store.dispatch(
              showError({ message: error.response.data?.message })
            );
            break;
          case HttpStatusCode.BadGateway:
            store.dispatch(
              showError({ message: error.response.data?.message })
            );
            break;
          default:
            store.dispatch(showError({ message: error.response.status }));
            break;
        }

        return Promise.reject(error);
      }
    );
  }

  request<T>(
    method: Enums.HTTP_METHOD,
    path: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ) {
    switch (method) {
      case Enums.HTTP_METHOD.POST:
        return this.axiosInstance.post<T>(path, data, config);
      case Enums.HTTP_METHOD.PUT:
        return this.axiosInstance.put<T>(path, data, config);
      case Enums.HTTP_METHOD.DELETE:
        return this.axiosInstance.delete<T>(path, config);
      case Enums.HTTP_METHOD.PATCH:
        return this.axiosInstance.patch<T>(path, data, config);
      case Enums.HTTP_METHOD.GET:
      default:
        return this.axiosInstance.get<T>(path, config);
    }
  }
}

const axios = new Axios();

export default axios;
